/**
 * Feature Flags Configuration
 * Contrôle centralisé des fonctionnalités activées/désactivées
 * @version 1.1.0
 */

export const FEATURE_FLAGS = {
  // ===== Dons (Arbore) =====
  // Defaut FERME. Ce flag est le SOCLE du garde-fou per-org : useOrganizationFeatureFlags
  // retourne `true` pour toute cle inconnue, donc sans cette ligne, poser la cle
  // 'donations' allumerait les dons pour TOUTES les organisations d'un coup.
  // Ouverture au cas par cas via une ligne organization_feature_flags (cle 'donations').
  // La livraison des dons est manuelle (V1) : n'ouvrir qu'a une organisation prete.
  DONATIONS_ENABLED: false,

  // ===== Offres partenaires (boutique de reductions SLD / reduc-ce) =====
  // Defaut FERME, meme raison que DONATIONS_ENABLED : sans cette ligne, la cle
  // 'partner_offers' serait ouverte partout (isEnabled retourne `true` pour toute
  // cle inconnue). Couvre la VISIBILITE (listes, carte, compteurs) ET l'ACHAT.
  // Ouverture au cas par cas via une ligne organization_feature_flags
  // (cle 'partner_offers'). Certains contrats excluent la boutique partenaire
  // (Mutuelle Catalane : DPA art. 3, Stripe absent de l'annexe 1) : une nouvelle
  // organisation ne voit donc rien tant qu'on ne l'a pas ouverte explicitement.
  // Garde serveur miroir : public.org_partner_offers_enabled(org).
  PARTNER_OFFERS_ENABLED: false,

  // ===== Native Health Support (legacy flag name) =====
  // Ce flag garde son nom historique mais couvre maintenant le support santé
  // natif au sens large:
  // - plugin custom `window.kokenHealth` en priorité
  // - fallback legacy éventuel si présent
  // Fonctionne uniquement en mode app native (pas web/PWA)
  MEDIAN_HEALTH_BRIDGE_ENABLED: true,

  // ===== GPS arrière-plan (Mobilité Douce / trajets) =====
  // Tracking GPS natif en arrière-plan via le module natif Kōken (window.kokenLocation)
  COMMUTE_BACKGROUND_LOCATION_ENABLED: true,

  // ===== GPS arrière-plan (Objectifs/Goal) =====
  // Tracking GPS natif en arrière-plan via le module natif Kōken pour les objectifs
  // (course, marche, vélo). Indépendant du flag Commute.
  GOAL_BACKGROUND_LOCATION_ENABLED: true,

  // ===== Validation Methods =====
  // Validation par montre connectée (UI visible - fonctionne via Median Health Bridge)
  SMARTWATCH_VALIDATION_ENABLED: true,

  // Validation par Photo IA
  PHOTO_AI_VALIDATION_ENABLED: true,

  // Validation GPS/Location
  LOCATION_VALIDATION_ENABLED: true,

  // ===== Gamification =====
  // Système de boosts
  BOOSTS_ENABLED: true,

  // Streaks et freezes
  STREAKS_ENABLED: true,

  // Équipes — MISE EN SOMMEIL 2026-07 (Escouade/Squade cachée par défaut).
  // Réversible : repasser à true (ou poser une ligne organization_feature_flags
  // 'teams' per-org). isEnabled('teams') (DesktopSidebar, MobileBottomNav
  // manager, SDGTrackingWidget) retombe sur ce global tant qu'aucune ligne DB
  // n'existe. La route /employee/squade reste montée (deep-links préservés).
  TEAMS_ENABLED: false,

  // Escouade V1 (refonte 2026-07-18) — le bloc « Mon équipage » dans Ma semaine :
  // Cap hebdo conjonctif, flamme d'escouade, Pari d'équipage. DISTINCT de
  // TEAMS_ENABLED (qui gouverne l'ANCIENNE page Squade, laissée en sommeil).
  // Défaut OFF partout ; activation pilote per-org via organization_feature_flags
  // clé 'squad_v1' (banc Kōken France d'abord). Réf : artefact « L'Escouade,
  // deuxième vie » + migration 20260718090000_escouade_v1_schema_rpc.sql.
  SQUAD_V1_ENABLED: false,

  // ===== Other Features =====
  // Mode debug (logs supplémentaires)
  DEBUG_MODE: import.meta.env.DEV,

  // ===== GPS Stats Fallback =====
  // Afficher les stats GPS au lieu du placeholder santé quand HealthKit non disponible
  GPS_STATS_FALLBACK_ENABLED: true,

  // ===== Health Bridge Settings =====
  // Afficher les paramètres de connexion santé dans le profil.
  // ✅ 2026-04-11: activé — notre plugin custom `KokenHealthKit` (iOS) est
  // live sur device physique. Le panneau utilise useHealthBridge qui route
  // vers window.kokenHealth (iOS) et bientôt window.kokenHealthAndroid.
  // Koken ne souscrit PAS au plan payant Median HealthBridge — stratégie
  // santé 100 % custom plugins natifs.
  HEALTH_BRIDGE_SETTINGS_ENABLED: true,

  // ===== UI Experiments (A/B test per organization) =====
  // Défaut `false` pour neutraliser le fallback `true` par défaut dans
  // useOrganizationFeatureFlags.isEnabled — la V2 ne doit s'activer que
  // lorsqu'une ligne explicite existe dans organization_feature_flags.
  UI_CSE_V2_ENABLED: false,
  /** @deprecated 2026-05-09 — V2 RSE forcée partout, V1 supprimée. Flag inerte côté routage. */
  UI_RSE_V2_ENABLED: false,
  UI_EMPLOYEE_V2_ENABLED: false,
  PLATFORM_ADAPTIVE_UX_ENABLED: false,

  // Carpool drawer V2 — refonte System 1 avec "Tes habituels" + stories rondes

  // ===== diamant Expiration =====
  // SUPPRIMÉ DÉFINITIVEMENT 15/06/2026 — flag retiré, crons + edge fns +
  // composants supprimés, contrainte DB wallet_transactions_no_expiry_chk
  // empêche toute régression. Voir mem://features/wallet/doken-expiration-system-v1.

  // ===== Feed autogénéré (fil d'activité) — EN SOMMEIL 2026-07 =====
  // La surface de lecture du feed a déjà été supprimée (route → home, aucun
  // composant monté). Ce flag coupe en plus le chemin d'ÉCRITURE : quand false,
  // publishActivity() ne fait plus d'INSERT dans activity_feed (les ~23
  // appelants restent inchangés). Réversible : repasser à true pour
  // ré-alimenter le feed le jour où on le réveille.
  ACTIVITY_FEED_ENABLED: false,

  // ===== FMD sur la landing publique — EN SOMMEIL 2026-07 =====
  // false = la section mobilité de la landing passe en « Bientôt disponible »
  // (promesse au futur, formulaire de contact conservé) et les mentions FMD
  // secondaires (FAQ B2B, axe planète) sont atténuées. Réversible : true
  // réaffiche le discours FMD « remboursés » complet.
  LANDING_FMD_ENABLED: false,

  // ===== Ma semaine (fusion Objectifs + Défis) — PILOTE A/B 2026-07 =====
  // Nouvelle page /employee/ma-semaine, ADDITIVE. Défaut OFF (global) :
  // s'active per-org via organization_feature_flags (clé 'ui_ma_semaine_v1').
  // Quand ON pour un org : la navbar fusionne Défis + Objectifs en un seul
  // onglet « Ma semaine ». Les pages Objectifs et Défis restent intactes
  // (rollback = flag off).
  UI_MA_SEMAINE_V1_ENABLED: false,

  // ===== Arcade V1 — Ligue de régularité du mouvement — PILOTE 2026-07 =====
  // Refonte de l'ancien mode Arcade GPS (0 session/30 j) en « régularité du
  // mouvement » : bouger tous les jours, score serveur anti-triche (baseline
  // médiane 28 j, plancher, cliquet), solo « ma semaine » d'abord. ADDITIVE :
  // quand OFF, l'ancienne vue ArcadeLeaderboardView reste. S'active per-org via
  // organization_feature_flags (clé 'arcade_v1', Kōken France en pilote).
  // ⚠️ OBLIGATOIRE ici (défaut OFF) : sans cette entrée, isEnabled('arcade_v1')
  // retombe sur le défaut `true` et serait ON partout.
  // Endormi 2026-07 — voir ARCADE_ENABLED juste en dessous : ce flag-ci n'est
  // qu'une BASCULE entre l'ancienne vue GPS et la ligue V1, il ne cache rien.
  ARCADE_V1_ENABLED: false,

  // ===== Arcade (TOUTES versions) — MISE EN SOMMEIL 2026-07 =====
  // Flag MAÎTRE : coupe l'Arcade en entier, l'ancienne vue GPS
  // (ArcadeLeaderboardView) comme la ligue de régularité V1
  // (ArcadeRegularityView). À ne pas confondre avec ARCADE_V1_ENABLED, qui ne
  // fait que choisir LAQUELLE des deux s'affiche.
  //
  // Ce que `false` coupe : le bouton manette de la page Classement (la seule
  // vraie porte d'entrée, elle n'avait AUCUN gating), le deep-link
  // ?view=arcade, le rendu du mode Arcade, et la tuile Arcade de l'Accueil.
  // Ce qui RESTE monté : la route /employee/leaderboard, les composants et
  // hooks Arcade, les services, les RPC et les tables.
  //
  // Volontairement ABSENT de AVAILABLE_FEATURE_FLAGS : on n'expose pas un
  // interrupteur admin pour une fonctionnalité qu'on endort.
  // Réversible : repasser à true (ou poser une ligne organization_feature_flags
  // 'arcade' per-org).
  ARCADE_ENABLED: false,

  // ===== Rituel du mois (Home salarié) — 2026-08 =====
  // Carte « Le Rituel du mois » : 4 médaillons = les semaines fixes du mois,
  // remplis selon les jours d'activité (RPC get_my_weekly_ritual, en prod).
  // ADDITIF sur la Home Refined, zone « Ma semaine ». `true` est VOULU ici
  // (défaut global ON, pas de gating per-org) : rollback = repasser à false.
  RITUAL_CARD_ENABLED: true,

} as const;

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof typeof FEATURE_FLAGS): boolean {
  return FEATURE_FLAGS[feature] ?? false;
}
